import React, { useCallback, useState } from 'react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import Avatar from '../Avatar';
import Icon from '../Icon';
import Button from '../Button';
import Dialog from '../Dialog';
import Logo, { LogoMark } from '../Logo';
import NotificationsButton from '../NotificationsButton';
import ThemeProvider from '../theme/ThemeProvider';
import ThemeToggle from '../ThemeToggle';
import { AppBarBack, AppBarContext, RequestLogoutContext, TabBarContext } from './AppBarContext';
import SideNav from './SideNav';
import { TabNavLink } from './TabNav';
import { NavItem, ShellUser } from './types';

interface AppShellProps {
  items: NavItem[];
  user: ShellUser;
  /** Where the tablet rail's avatar links to (the account page). */
  accountPath: string;
  /** Body text of the logout confirmation. */
  logoutDescription: string;
  onLogout: () => Promise<void> | void;
  children: React.ReactNode;
}

/**
 * Dashboard frame from the Figma screens, mobile-first:
 *  - mobile  (<768):  sticky app bar (logo, theme, bell) + fixed bottom tab bar
 *  - tablet  (768+):  96px icon rail on the left
 *  - desktop (1024+): 264px sidebar with WhatsApp card and the signed-in user
 */
const AppShell: React.FC<AppShellProps> = ({ items, user, accountPath, logoutDescription, onLogout, children }) => {
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [back, setBack] = useState<AppBarBack | null>(null);
  const [tabBarHidden, setTabBarHidden] = useState(false);
  const openLogout = useCallback(() => setConfirmLogout(true), []);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await onLogout();
    } finally {
      setLoggingOut(false);
      setConfirmLogout(false);
    }
  };

  return (
    <ThemeProvider className="tw-flex tw-min-h-dvh tw-bg-ds-canvas tw-text-ds-text-primary">
      <SideNav items={items} user={user} onLogout={openLogout} />

      {/* Tablet rail */}
      <aside className="tw-sticky tw-top-0 tw-hidden tw-h-dvh tw-w-24 tw-shrink-0 tw-flex-col tw-items-center tw-gap-1.5 tw-overflow-y-auto tw-border-r tw-border-ds-border-default tw-bg-ds-surface tw-px-2.5 tw-py-6 md:tw-flex lg:tw-hidden">
        <LogoMark />
        <nav aria-label="Main" className="tw-mt-5 tw-flex tw-w-full tw-flex-col tw-gap-1.5">
          {items.map((item) => (
            <React.Fragment key={item.to}>
              {item.group === 'account' && <span aria-hidden className="tw-h-2" />}
              <TabNavLink item={item} variant="rail" />
            </React.Fragment>
          ))}
        </nav>
        <div className="tw-flex-1" />
        <Link to={accountPath} aria-label="Account" className="tw-rounded-full focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand">
          <Avatar name={user.name} />
        </Link>
      </aside>

      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
        {/* Mobile app bar */}
        <header className="tw-sticky tw-top-0 tw-z-30 tw-flex tw-items-center tw-justify-between tw-bg-ds-canvas tw-px-4 tw-py-2 md:tw-hidden">
          {back ? (
            <Link
              to={back.to}
              className="tw-flex tw-items-center tw-gap-1 tw-rounded-lg tw-text-[17px] tw-font-semibold tw-leading-6 tw-text-ds-text-primary focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
            >
              <Icon icon={ChevronLeft} size={22} />
              {back.label}
            </Link>
          ) : (
            <Logo size="xs" />
          )}
          <div className="tw-flex tw-items-center tw-gap-2">
            <ThemeToggle />
            <NotificationsButton />
          </div>
        </header>

        <main
          className={cn(
            'tw-flex-1 tw-px-4 tw-pt-3 md:tw-p-8 lg:tw-px-12 lg:tw-pb-12 lg:tw-pt-9',
            tabBarHidden ? 'tw-pb-[calc(24px+env(safe-area-inset-bottom))]' : 'tw-pb-[calc(96px+env(safe-area-inset-bottom))]',
          )}
        >
          <AppBarContext.Provider value={setBack}>
            <TabBarContext.Provider value={setTabBarHidden}>
              <RequestLogoutContext.Provider value={openLogout}>{children}</RequestLogoutContext.Provider>
            </TabBarContext.Provider>
          </AppBarContext.Provider>
        </main>
      </div>

      {/* Mobile tab bar */}
      <nav
        aria-label="Main"
        className={cn(
          'tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-30 tw-border-t tw-border-ds-border-default tw-bg-ds-surface tw-px-1 tw-pt-2 tw-pb-[calc(8px+env(safe-area-inset-bottom))] md:tw-hidden',
          tabBarHidden ? 'tw-hidden' : 'tw-flex',
        )}
      >
        {items.map((item) => (
          <TabNavLink key={item.to} item={item} variant="bar" />
        ))}
      </nav>

      <Dialog
        open={confirmLogout}
        onOpenChange={setConfirmLogout}
        title="Log out of Protega?"
        description={logoutDescription}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmLogout(false)} disabled={loggingOut}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleLogout} loading={loggingOut}>
              Log out
            </Button>
          </>
        }
      />
    </ThemeProvider>
  );
};

export default AppShell;
